import { LoadingOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Form, message, Modal, Select, Space, Tag, Typography } from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

const { Text, Title } = Typography;
const { Option } = Select;

const SendCandidateCallModal = ({ visible, onCancel, candidate }) => {
  const [form] = Form.useForm();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [callLoading, setCallLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const normalizeJobs = (payload) => {
    const rawJobs = Array.isArray(payload?.job_descriptions)
      ? payload.job_descriptions
      : (Array.isArray(payload?.jobs) ? payload.jobs : []);

    return rawJobs.map((job) => ({
      id: job?.id || '',
      title: job?.title || 'Untitled Role',
      company: job?.company || 'Unknown company',
      description: job?.description || '',
      location: job?.location || '',
      status: job?.status || 'active'
    })).filter((job) => !!job.id);
  };

  // Fetch available jobs
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('Token for jobs fetch:', token ? 'Token exists' : 'No token found');

      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const response = await axios.get('/api/job_descriptions?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        const normalizedJobs = normalizeJobs(response.data);
        setJobs(normalizedJobs);
        console.log('[SendCandidateCallModal] fetched jobs:', {
          count: normalizedJobs.length,
          preview: normalizedJobs.slice(0, 3).map((j) => ({ id: j.id, title: j.title }))
        });
        if (normalizedJobs.length === 0) {
          message.warning('No jobs available for your account yet. Please create or upload a job first.');
        }
      } else {
        message.error('Failed to fetch jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      message.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Handle job selection
  const handleJobSelect = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setSelectedJob(job);
  };

  // Handle call initiation
  const handleInitiateCall = async () => {
    if (!selectedJob) {
      message.error('Please select a job first');
      return;
    }

    if (!candidate.phoneNumbers || candidate.phoneNumbers.length === 0) {
      message.error('No phone number available for this candidate');
      return;
    }

    try {
      setCallLoading(true);
      const token = localStorage.getItem('token');
      console.log('Token for call initiation:', token ? 'Token exists' : 'No token found');

      if (!token) {
        message.error('No authentication token found. Please log in again.');
        return;
      }

      const response = await axios.post('/api/candidates/initiate-call', {
        candidate_id: candidate.id,
        job_id: selectedJob.id,
        candidate_name: candidate.contactName,
        candidate_phone: candidate.phoneNumbers[0],
        candidate_email: candidate.email,
        candidate_title: candidate.job_title || candidate.title,
        candidate_skills: candidate.skills || [],
        candidate_experience: candidate.workExperience || []
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success(`Call initiated successfully! Call ID: ${response.data.call_id || 'N/A'}`);
        form.resetFields();
        setSelectedJob(null);

        // Call onSuccess callback if provided (to refresh candidate list)
        if (onCancel && typeof onCancel === 'function') {
          onCancel(true); // Pass true to indicate successful call
        } else {
          onCancel();
        }
      } else {
        const errorMsg = response.data.error || 'Failed to initiate call';
        message.error(errorMsg);
        console.error('Call initiation error:', response.data.error_details || errorMsg);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initiate call';
      message.error(errorMessage);
    } finally {
      setCallLoading(false);
    }
  };

  // Format location for display
  const formatLocation = (location) => {
    if (!location) return 'Not specified';

    if (typeof location === 'object') {
      const city = location.city || '';
      const country = location.country || '';
      return city && country ? `${city}, ${country}` : city || country || 'Not specified';
    }

    return location;
  };

  useEffect(() => {
    if (visible) {
      fetchJobs();
    }
  }, [visible]);

  if (!candidate) return null;

  return (
    <Modal
      title={
        <Space>
          <PhoneOutlined style={{ color: '#52c41a' }} />
          <span>Initiate Call to {candidate.contactName}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <div style={{ marginBottom: '24px' }}>
        <Title level={5}>Candidate Information</Title>
        <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '6px' }}>
          <div><strong>Name:</strong> {candidate.contactName}</div>
          <div><strong>Current Title:</strong> {candidate.job_title || candidate.title || 'Not specified'}</div>
          <div><strong>Location:</strong> {formatLocation(candidate.location)}</div>
          <div><strong>Phone:</strong> {candidate.phoneNumbers?.[0] || 'Not available'}</div>
          <div><strong>Email:</strong> {candidate.email || 'Not available'}</div>
        </div>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          label="Select Job"
          name="job_id"
          rules={[{ required: true, message: 'Please select a job' }]}
        >
          <Select
            placeholder="Select a job to call about"
            onChange={handleJobSelect}
            loading={loading}
            disabled={jobs.length === 0}
          >
            {jobs.map(job => (
              <Option key={job.id} value={job.id}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{job.title}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {job.company} • {job.location}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedJob && (
          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Selected Job</Title>
            <div style={{ background: '#f0f8ff', padding: '16px', borderRadius: '6px' }}>
              <div><strong>Title:</strong> {selectedJob.title}</div>
              <div><strong>Company:</strong> {selectedJob.company}</div>
              <div><strong>Location:</strong> {selectedJob.location}</div>
              <div><strong>Status:</strong>
                <Tag color={selectedJob.status === 'active' ? 'green' : 'orange'} style={{ marginLeft: '8px' }}>
                  {selectedJob.status}
                </Tag>
              </div>
            </div>
          </div>
        )}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={callLoading ? <LoadingOutlined /> : <PhoneOutlined />}
              onClick={handleInitiateCall}
              loading={callLoading}
              disabled={!selectedJob || !candidate.phoneNumbers?.length}
            >
              {callLoading ? 'Initiating Call...' : 'Initiate Call'}
            </Button>
            <Button onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {!candidate.phoneNumbers?.length && (
        <div style={{ marginTop: '16px', padding: '12px', background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: '6px' }}>
          <Text type="warning">
            <PhoneOutlined /> No phone number available for this candidate.
            Please add a phone number to the candidate profile before making a call.
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default SendCandidateCallModal;
